import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { HiCheck } from "react-icons/hi";
import { AiFillDelete } from "react-icons/ai";

import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import SelectComponent from "../../../components/Tenant/SelectComponent/SelectComponent";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import { getVendorName } from "../../../utils/getVendorName";
import {
  useActivateMembershipMutation,
  useCreateMembershipMutation,
  useFetchMembershipByIdMutation,
  useFetchMembershipLockStatusMutation,
  useUpdateMembershipByIdMutation,
} from "../../../features/tenant/membership/membershipApi";
import {
  useCreateMembershipDetailsMutation,
  useUpdateMembershipDetailsMutation,
} from "../../../features/tenant/membershipDetails/membershipDetailsApi";
import { useGetAppUserTypesMutation } from "../../../features/tenant/appUsers/appUsersApi";
import { useFetchAllSchemeMutation } from "../../../features/tenant/scheme/schemeApi";
import { listDrafts, saveDraft, deleteDraft } from "./membershipDrafts";

/**
 * Create Scheme Membership — single wizard.
 *
 * Replaces the old two-screen Add -> Manage flow. That split was a problem once
 * the config became immutable: a membership could go live half-configured. Here
 * everything is gathered first, reviewed, then created and ACTIVATED in one go.
 *
 * Only four of the eight mode combinations are legal, and the UI enforces it
 * rather than letting the API reject it later:
 *   lifetime + realtime      + fixed
 *   periodic + realtime      + fixed
 *   periodic + end_of_period + fixed | percentage
 *
 * Doubles as the UPDATE screen (navigated with location.state.id). A config that
 * users have already accrued against is frozen, so edit mode asks the API first
 * and refuses up front rather than letting someone fill in the whole form and
 * eat a 409 on save.
 */

const STEPS = [
  { label: "Scope", desc: "Who it applies to and which primary scheme it runs on." },
  { label: "Modes", desc: "How progress accrues and when the reward is paid." },
  { label: "Slabs", desc: "Point bands and what each one rewards." },
  { label: "Review", desc: "Confirm, then create and activate." },
];

const PERIOD_OPTIONS = [
  { days: 30, label: "30 Days", hint: "Monthly" },
  { days: 45, label: "45 Days", hint: "Mid-cycle" },
  { days: 90, label: "90 Days", hint: "Quarterly" },
  { days: 180, label: "180 Days", hint: "Half-yearly" },
];

const inputClass =
  "block w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all";

const SectionCard = ({ title, desc, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
    {(title || desc) && (
      <div className="mb-5 pb-3 border-b border-gray-100">
        {title && <h2 className="text-lg font-semibold text-gray-900">{title}</h2>}
        {desc && <p className="text-sm text-gray-500 mt-1">{desc}</p>}
      </div>
    )}
    {children}
  </div>
);

const WizardStepper = ({ steps, current, onStep }) => (
  <div className="flex items-center">
    {steps.map((s, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <div key={s.label} className={`flex items-center ${i < steps.length - 1 ? "flex-1" : ""}`}>
          <button type="button" onClick={() => onStep(i)} className="flex items-center gap-2 shrink-0 group">
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-all ${
                active
                  ? "bg-blue-600 text-white ring-4 ring-blue-100"
                  : done
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-400 border border-gray-300 group-hover:border-gray-400"
              }`}
            >
              {done ? <HiCheck className="h-4 w-4" /> : i + 1}
            </span>
            <span
              className={`text-sm hidden md:block ${
                active ? "font-semibold text-gray-900" : done ? "font-medium text-gray-600" : "text-gray-400"
              }`}
            >
              {s.label}
            </span>
          </button>
          {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 md:mx-3 rounded ${done ? "bg-blue-600" : "bg-gray-200"}`} />}
        </div>
      );
    })}
  </div>
);

/** Segmented choice. Options may be disabled with a reason shown as a tooltip. */
const Segmented = ({ value, onChange, options }) => (
  <div className="inline-flex rounded-lg border border-gray-300 bg-gray-50 p-1">
    {options.map((opt) => (
      <button
        key={opt.key}
        type="button"
        title={opt.disabledReason || ""}
        disabled={!!opt.disabledReason}
        onClick={() => onChange(opt.key)}
        className={`px-4 py-1.5 text-sm rounded-md transition-all ${
          value === opt.key
            ? "bg-white text-gray-900 font-semibold shadow-sm"
            : opt.disabledReason
            ? "text-gray-300 cursor-not-allowed"
            : "text-gray-600 hover:text-gray-900"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const fmtDay = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

/**
 * Slice the scheme window into periods for a preview, so the tenant can see how
 * many payouts their choice implies before committing to a frozen config.
 * Mirrors the engine's floor() indexing; capped so a long scheme with short
 * periods doesn't render hundreds of rows.
 */
const buildPeriods = (start, end, periodDays) => {
  if (!start || !end || !periodDays) return [];
  const MS = 86400000;
  const s = Date.parse(`${start}T00:00:00Z`);
  const e = Date.parse(`${end}T00:00:00Z`) + MS;
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return [];
  const out = [];
  for (let i = 0; i < 60; i++) {
    const ps = s + i * periodDays * MS;
    if (ps >= e) break;
    const pe = Math.min(ps + periodDays * MS, e);
    out.push({
      index: i,
      start: new Date(ps).toISOString().slice(0, 10),
      end: new Date(pe - MS).toISOString().slice(0, 10),
      partial: pe - ps < periodDays * MS,
    });
  }
  return out;
};

const emptySlab = () => ({ name: "", rangeStart: 0, rangeEnd: 0, reward: 0 });

const MembershipWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const vendorData = JSON.parse(sessionStorage.getItem("vendorData")) || {};
  const token = sessionStorage.getItem("access_token");

  const membershipId = location.state?.id ?? null;
  const isEdit = Boolean(membershipId);

  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  // Snapshot of the saved config, for the diff on the review step.
  const [original, setOriginal] = useState(null);

  // Step 1 — scope
  const [selectedUser, setSelectedUser] = useState({ id: -1, name: "Select User Type" });
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [schemeList, setSchemeList] = useState([]);

  // Step 2 — modes
  const [mode, setMode] = useState("lifetime");
  const [payoutMode, setPayoutMode] = useState("realtime");
  const [rewardType, setRewardType] = useState("fixed");
  const [periodDays, setPeriodDays] = useState(30);

  // Step 3 — slabs
  const [slabs, setSlabs] = useState([emptySlab()]);

  // Drafts
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [drafts, setDrafts] = useState([]);

  const [createMembership] = useCreateMembershipMutation();
  const [createMembershipDetails] = useCreateMembershipDetailsMutation();
  const [updateMembershipById] = useUpdateMembershipByIdMutation();
  const [updateMembershipDetails] = useUpdateMembershipDetailsMutation();
  const [activateMembership] = useActivateMembershipMutation();
  const [fetchMembershipById] = useFetchMembershipByIdMutation();
  const [fetchLockStatus] = useFetchMembershipLockStatusMutation();
  const [getAppUserTypes, { data: appUserTypesData }] = useGetAppUserTypesMutation();
  const [fetchAllScheme, { data: schemeData }] = useFetchAllSchemeMutation();

  useEffect(() => {
    getAppUserTypes({ tenant_id: vendorData.tenant_id });
    fetchAllScheme({
      token,
      tenant_id: vendorData.tenant_id,
      body: { limit: 500, offset: 0, status: "1" },
    });
    setDrafts(listDrafts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // /tenant/scheme returns { body: { data: [...], count } } — not a bare array.
    const rows = schemeData?.body?.data;
    setSchemeList(Array.isArray(rows) ? rows : []);
  }, [schemeData]);

  // Edit mode: ask whether the config is still editable BEFORE prefilling, then
  // load it. Slab rows keep their membership_details id so the save can go
  // through batchUpdate rather than delete-and-recreate.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;

    (async () => {
      try {
        const [lock, detail] = await Promise.all([
          fetchLockStatus({ token, tenant_id: vendorData.tenant_id, id: membershipId }).unwrap(),
          fetchMembershipById({ token, tenant_id: vendorData.tenant_id, id: membershipId }).unwrap(),
        ]);
        if (cancelled) return;

        setLockInfo(lock?.body ?? null);

        const joinRows = detail?.body || [];
        const meta = joinRows[0];
        if (!meta) {
          toast.error("Membership not found", { id: "notFound" });
          return;
        }

        setSelectedUser({
          id: meta.user_type_id,
          name: meta.user_type,
          user_type: meta.user_type,
          user_type_id: meta.user_type_id,
        });
        setMode(meta.mode || "lifetime");
        setPayoutMode(meta.payout_mode || "realtime");
        setRewardType(meta.reward_type || "fixed");
        if (meta.period_days) setPeriodDays(Number(meta.period_days));

        // Order by range_start — the value the engine resolves on. Rows with a
        // null tier mean no slabs saved yet (LEFT JOIN).
        const loaded = joinRows
          .filter((r) => r && r.tier)
          .map((r) => ({
            detailId: r.id,
            name: r.tier?.name ?? "",
            rangeStart: Number(r.range_start ?? 0),
            rangeEnd: Number(r.range_end ?? 0),
            reward: Number(r.tier?.bonus ?? 0),
          }))
          .sort((a, b) => a.rangeStart - b.rangeStart);

        if (loaded.length) setSlabs(loaded);
        setOriginal({
          mode: meta.mode || "lifetime",
          payoutMode: meta.payout_mode || "realtime",
          rewardType: meta.reward_type || "fixed",
          periodDays: Number(meta.period_days) || null,
          slabs: loaded,
          schemeId: meta.scheme_id,
        });
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.data?.message || "Could not load this membership", {
            id: "loadFailed",
          });
        }
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, membershipId]);

  // The scheme dropdown loads separately; match the saved scheme once it arrives.
  useEffect(() => {
    if (!isEdit || !original?.schemeId || selectedScheme) return;
    const match = schemeList.find((x) => String(x.id) === String(original.schemeId));
    if (match) setSelectedScheme(match);
  }, [isEdit, original, schemeList, selectedScheme]);

  // Lifetime has no period boundary to settle on, so payout is forced back to
  // realtime — and percentage needs a settled total, so it follows.
  useEffect(() => {
    if (mode === "lifetime" && payoutMode !== "realtime") setPayoutMode("realtime");
  }, [mode, payoutMode]);

  useEffect(() => {
    if (payoutMode !== "end_of_period" && rewardType !== "fixed") setRewardType("fixed");
  }, [payoutMode, rewardType]);

  const primarySchemes = useMemo(
    () =>
      (Array.isArray(schemeList) ? schemeList : []).filter(
        (s) => s.is_primary === true || s.is_primary === "true",
      ),
    [schemeList],
  );

  const startDate = fmtDay(selectedScheme?.scheme_start);
  const endDate = fmtDay(selectedScheme?.scheme_end);
  const periods = useMemo(
    () => (mode === "periodic" ? buildPeriods(startDate, endDate, Number(periodDays)) : []),
    [mode, startDate, endDate, periodDays],
  );

  const rewardLabel = rewardType === "percentage" ? "Reward %" : "Reward Points";

  // ---- validation -----------------------------------------------------------

  const validateScope = () => {
    if (selectedUser.id === -1) return "Select a user type";
    if (!selectedScheme) return "Select a primary scheme";
    if (!startDate || !endDate) return "The selected scheme has no run window";
    return null;
  };

  const validateModes = () => {
    if (mode === "periodic" && !PERIOD_OPTIONS.some((p) => p.days === Number(periodDays))) {
      return "Select a valid period length";
    }
    return null;
  };

  const canEditScope = !isEdit || !lockInfo?.activated;
  const canChangeSlabCount = !isEdit;

  const validateSlabs = () => {
    if (slabs.length === 0) return "Add at least one slab";
    const sorted = [...slabs].sort((a, b) => Number(a.rangeStart) - Number(b.rangeStart));
    for (let i = 0; i < sorted.length; i++) {
      const s = sorted[i];
      if (!String(s.name || "").trim()) return "Every slab needs a name";
      if (Number(s.rangeStart) < 0 || Number(s.rangeEnd) < 0) return "Ranges cannot be negative";
      if (Number(s.rangeEnd) <= Number(s.rangeStart)) return `"${s.name}": range end must be greater than range start`;
      if (Number(s.reward) < 0) return `"${s.name}": reward cannot be negative`;
      if (rewardType === "percentage" && Number(s.reward) > 100) {
        return `"${s.name}": a percentage reward cannot exceed 100`;
      }
      if (i > 0 && Number(s.rangeStart) < Number(sorted[i - 1].rangeEnd)) {
        return `"${s.name}" overlaps "${sorted[i - 1].name}"`;
      }
    }
    // In end-of-period mode only the highest slab reached pays, so a higher slab
    // worth less than a lower one silently penalises the better user.
    if (payoutMode === "end_of_period") {
      for (let i = 1; i < sorted.length; i++) {
        if (Number(sorted[i].reward) < Number(sorted[i - 1].reward)) {
          return `"${sorted[i].name}" rewards less than "${sorted[i - 1].name}" — in end-of-period mode only the highest slab reached is paid`;
        }
      }
    }
    return null;
  };

  const validateStep = (step) => {
    const error = [validateScope, validateModes, validateSlabs, () => null][step]();
    if (error) {
      toast.error(error, { id: "wizardStep" });
      return false;
    }
    return true;
  };

  // ---- drafts ---------------------------------------------------------------

  const buildDraftData = () => ({
    selectedUser,
    selectedScheme,
    mode,
    payoutMode,
    rewardType,
    periodDays,
    slabs,
  });

  const persistDraft = (step = currentStep) => {
    if (selectedUser.id === -1 && !selectedScheme) return;
    const id = activeDraftId || Date.now();
    const saved = saveDraft({
      id,
      name: selectedScheme ? `${selectedUser.name} · ${selectedScheme.name}` : "Untitled draft",
      currentStep: step,
      data: buildDraftData(),
    });
    setActiveDraftId(id);
    setLastSavedAt(saved.updatedAt);
    setDrafts(listDrafts());
  };

  const applyDraft = (d) => {
    const data = d.data || {};
    setSelectedUser(data.selectedUser || { id: -1, name: "Select User Type" });
    setSelectedScheme(data.selectedScheme || null);
    setMode(data.mode || "lifetime");
    setPayoutMode(data.payoutMode || "realtime");
    setRewardType(data.rewardType || "fixed");
    setPeriodDays(data.periodDays || 30);
    setSlabs(Array.isArray(data.slabs) && data.slabs.length ? data.slabs : [emptySlab()]);
    setActiveDraftId(d.id);
    setCurrentStep(d.currentStep || 0);
    setShowDrafts(false);
  };

  const goToStep = (target) => {
    persistDraft(target);
    setCurrentStep(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleNext = () => {
    if (validateStep(currentStep)) goToStep(currentStep + 1);
  };
  const handleBack = () => goToStep(Math.max(0, currentStep - 1));

  // ---- submit ---------------------------------------------------------------

  /**
   * Save an existing membership: the config row, then the slab rows by id.
   *
   * No activate call — an already-activated membership stays activated, and a
   * draft is activated explicitly from the review step so the tenant chooses
   * when the clock starts.
   */
  const handleUpdate = async () => {
    await updateMembershipById({
      token,
      tenant_id: vendorData.tenant_id,
      id: membershipId,
      body: {
        user_type_id: selectedUser.user_type_id,
        user_type: selectedUser.user_type,
        range_type: 4,
        scheme_id: selectedScheme?.id,
        mode,
        payout_mode: payoutMode,
        reward_type: rewardType,
        ...(mode === "periodic" ? { period_days: Number(periodDays) } : {}),
      },
    }).unwrap();

    const ordered = [...slabs].sort(
      (a, b) => Number(a.rangeStart) - Number(b.rangeStart),
    );
    const byRankLen = ordered.length;

    await updateMembershipDetails({
      token,
      tenant_id: vendorData.tenant_id,
      body: {
        membership_id: membershipId,
        batch: ordered.map((slab, i) => ({
          id: slab.detailId,
          tier: {
            // rank 1 = best, same convention as create.
            rank: byRankLen - i,
            name: slab.name,
            bonus_type: rewardType,
            bonus: Number(slab.reward),
          },
          range_start: Number(slab.rangeStart),
          range_end: Number(slab.rangeEnd),
        })),
      },
    }).unwrap();

    toast.success("Membership updated", { id: "membershipUpdated" });
    navigate(`/genefied/${getVendorName()}/membership`);
  };

  const handleSubmit = async () => {
    for (let step = 0; step < 3; step++) {
      if (!validateStep(step)) {
        setCurrentStep(step);
        return;
      }
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await handleUpdate();
        return;
      }
      // Sort ONCE, here, and use the same order for both writes. range_start is
      // what the engine actually orders by; rank is display-only. If these two
      // payloads disagreed, membership.tiers would rank slabs differently from
      // membership_details, which is exactly the rank corruption ManageMembership
      // has had to heal in the past.
      const ordered = [...slabs].sort(
        (a, b) => Number(a.rangeStart) - Number(b.rangeStart),
      );

      // RANK 1 = BEST tier, which is this codebase's convention everywhere:
      // membershipFunc.oneLevelDown treats rank+1 as worse, and
      // MembershipHIstory renders a green up-arrow when the rank DECREASES.
      // Numbering the lowest slab as rank 1 would invert those arrows and show
      // every promotion as a downgrade. Payouts are unaffected either way —
      // DefaultStrategy.resolveTier orders on range_start and ignores rank.
      const byRank = [...ordered].reverse(); // highest slab first
      const rankFor = (i) => ordered.length - i; // ordered is ascending

      const created = await createMembership({
        token,
        tenant_id: vendorData.tenant_id,
        body: {
          user_type_id: selectedUser.user_type_id,
          user_type: selectedUser.user_type,
          // Scheme membership always progresses on points.
          range_type: 4,
          tiers: byRank.map((s, i) => ({ id: i + 1, name: s.name })),
          scheme_id: selectedScheme.id,
          mode,
          payout_mode: payoutMode,
          reward_type: rewardType,
          ...(mode === "periodic" ? { period_days: Number(periodDays) } : {}),
        },
      }).unwrap();

      const membershipId = created?.body?.id ?? created?.data?.id;
      if (!membershipId) throw new Error("Membership was created but no id came back");

      await createMembershipDetails({
        token,
        tenant_id: vendorData.tenant_id,
        body: {
          membership_id: membershipId,
          qty: ordered.length,
          data: ordered.map((s, i) => ({
            tier: {
              rank: rankFor(i),
              name: s.name,
              bonus_type: rewardType,
              bonus: Number(s.reward),
            },
            range_start: Number(s.rangeStart),
            range_end: Number(s.rangeEnd),
            per_month: 0,
            points: 0,
            gift_discount: 0,
            cashback: false,
            cash_benefit: 0,
          })),
        },
      }).unwrap();

      // Activation is what starts the clock. Until this succeeds the engine
      // ignores the config entirely, so a failure here leaves a safe draft
      // rather than a half-live membership.
      await activateMembership({
        token,
        tenant_id: vendorData.tenant_id,
        id: membershipId,
      }).unwrap();

      if (activeDraftId) deleteDraft(activeDraftId);
      toast.success("Membership created and activated", { id: "membershipCreated" });
      navigate(`/genefied/${getVendorName()}/membership`);
    } catch (err) {
      const message =
        err?.data?.message || err?.message || "Could not create the membership";
      toast.error(message, { id: "membershipCreateFailed" });
    } finally {
      setSubmitting(false);
    }
  };

  // ---- steps ----------------------------------------------------------------

  const renderScope = () => (
    <SectionCard title="Scope" desc={STEPS[0].desc}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          {canEditScope ? (
            <SelectComponent
              title="User Type"
              selected={selectedUser}
              setSelected={setSelectedUser}
              dataArr={appUserTypesData?.body || []}
              required
            />
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
              <div className={`${inputClass} bg-gray-50 text-gray-500`}>{selectedUser.name}</div>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">
            Membership applies to this user type only. Other user types on the same scheme
            scan normally and accrue nothing.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Primary Scheme <span className="text-red-500">*</span>
          </label>
          {canEditScope ? (
            <select
              className={inputClass}
              value={selectedScheme?.id ?? ""}
              onChange={(e) => {
                const scheme = primarySchemes.find((s) => String(s.id) === e.target.value);
                setSelectedScheme(scheme || null);
              }}
            >
              <option value="">Select a primary scheme</option>
              {primarySchemes.map((s) => (
                <option key={s.id} value={s.id}>
                  #{s.id} — {s.name}
                </option>
              ))}
            </select>
          ) : (
            <div className={`${inputClass} bg-gray-50 text-gray-500`}>
              {selectedScheme ? `#${selectedScheme.id} — ${selectedScheme.name}` : "—"}
            </div>
          )}
          {primarySchemes.length === 0 && (
            <p className="text-xs text-amber-600 mt-2">
              No active primary scheme found. Mark a scheme as primary first.
            </p>
          )}
        </div>
      </div>

      {selectedScheme && (
        <div className="mt-5 grid grid-cols-2 gap-4 rounded-lg bg-gray-50 border border-gray-200 p-4">
          <div>
            <span className="block text-xs text-gray-500">Start</span>
            <span className="text-sm font-medium text-gray-900">{startDate}</span>
          </div>
          <div>
            <span className="block text-xs text-gray-500">End</span>
            <span className="text-sm font-medium text-gray-900">{endDate}</span>
          </div>
          <p className="col-span-2 text-xs text-gray-500">
            The run window is taken from the scheme and cannot be edited here.
          </p>
        </div>
      )}
    </SectionCard>
  );

  const renderModes = () => (
    <SectionCard title="Modes" desc={STEPS[1].desc}>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Progression Mode</label>
          <Segmented
            value={mode}
            onChange={setMode}
            options={[
              { key: "lifetime", label: "Lifetime" },
              { key: "periodic", label: "Period Based" },
            ]}
          />
          <p className="text-xs text-gray-500 mt-2">
            {mode === "lifetime"
              ? "Progress accumulates over the whole scheme and never resets."
              : "Progress resets to zero at the start of every period."}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Payout Mode</label>
          <Segmented
            value={payoutMode}
            onChange={setPayoutMode}
            options={[
              { key: "realtime", label: "Realtime" },
              {
                key: "end_of_period",
                label: "End of Period",
                disabledReason:
                  mode === "lifetime"
                    ? "Lifetime progression has no period boundary to settle on"
                    : "",
              },
            ]}
          />
          <p className="text-xs text-gray-500 mt-2">
            {payoutMode === "realtime"
              ? "Points are credited as the user crosses each slab, and every slab crossed pays."
              : "Nothing is credited during the period. The reward is settled once after the period closes, based on the highest slab reached, and is then final."}
          </p>
        </div>

        {mode === "periodic" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Period Length</label>
            <select
              className={`${inputClass} md:w-64`}
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value))}
            >
              {PERIOD_OPTIONS.map((p) => (
                <option key={p.days} value={p.days}>
                  {p.label} — {p.hint}
                </option>
              ))}
            </select>

            {periods.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-2">
                  {periods.length} period{periods.length === 1 ? "" : "s"} in this scheme
                  {periods.some((p) => p.partial) && " (the last one is shorter)"}.
                </p>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {periods.map((p) => (
                    <div
                      key={p.index}
                      className={`shrink-0 rounded-lg border px-3 py-2 text-xs ${
                        p.partial ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="font-semibold text-gray-700">P{p.index}</div>
                      <div className="text-gray-500">
                        {p.start} → {p.end}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );

  const renderSlabs = () => (
    <SectionCard title="Slabs" desc={STEPS[2].desc}>
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Reward Type</label>
        <Segmented
          value={rewardType}
          onChange={setRewardType}
          options={[
            { key: "fixed", label: "Fixed Points" },
            {
              key: "percentage",
              label: "Percentage",
              disabledReason:
                payoutMode !== "end_of_period"
                  ? "Percentage needs a settled period total, so it requires End of Period payout"
                  : "",
            },
          ]}
        />
        <p className="text-xs text-gray-500 mt-2">
          {rewardType === "percentage"
            ? "The reward is a percentage of the points scanned in the period, rounded down."
            : "The reward is a flat number of points."}
        </p>
      </div>

      <div className="space-y-3">
        {slabs.map((slab, index) => (
          <div
            key={index}
            className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end rounded-lg border border-gray-200 p-3"
          >
            <div className="md:col-span-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">Slab Name</label>
              <input
                className={inputClass}
                value={slab.name}
                placeholder="e.g. Gold"
                onChange={(e) => {
                  const next = [...slabs];
                  next[index] = { ...slab, name: e.target.value };
                  setSlabs(next);
                }}
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">From (points)</label>
              <input
                type="number"
                className={inputClass}
                value={slab.rangeStart}
                onChange={(e) => {
                  const next = [...slabs];
                  next[index] = { ...slab, rangeStart: e.target.value };
                  setSlabs(next);
                }}
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">To (points)</label>
              <input
                type="number"
                className={inputClass}
                value={slab.rangeEnd}
                onChange={(e) => {
                  const next = [...slabs];
                  next[index] = { ...slab, rangeEnd: e.target.value };
                  setSlabs(next);
                }}
              />
            </div>
            <div className="md:col-span-2 flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">{rewardLabel}</label>
                <input
                  type="number"
                  className={inputClass}
                  value={slab.reward}
                  onChange={(e) => {
                    const next = [...slabs];
                    next[index] = { ...slab, reward: e.target.value };
                    setSlabs(next);
                  }}
                />
              </div>
              {canChangeSlabCount && (
                <button
                  type="button"
                  title="Remove slab"
                  className="mb-1 p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                  onClick={() => setSlabs(slabs.filter((_, i) => i !== index))}
                >
                  <AiFillDelete className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {canChangeSlabCount ? (
        <button
          type="button"
          className="mt-4 px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors"
          onClick={() => setSlabs([...slabs, emptySlab()])}
        >
          + Add slab
        </button>
      ) : (
        <p className="mt-4 text-xs text-gray-500">
          Slabs can be re-valued but not added or removed here — the number of bands is part
          of the saved config. Need a different set? Create a new membership.
        </p>
      )}
    </SectionCard>
  );

  /** Field-level diff against the saved config, shown before an update is sent. */
  const computeChanges = () => {
    if (!isEdit || !original) return [];
    const out = [];
    const add = (label, before, after) => {
      if (String(before ?? "") !== String(after ?? "")) out.push({ label, before, after });
    };

    add("Progression", original.mode, mode);
    add("Payout", original.payoutMode, payoutMode);
    add("Reward type", original.rewardType, rewardType);
    if (mode === "periodic" || original.mode === "periodic") {
      add("Period length", original.periodDays, mode === "periodic" ? Number(periodDays) : null);
    }

    const byId = new Map(original.slabs.map((x) => [x.detailId, x]));
    for (const slab of slabs) {
      const before = byId.get(slab.detailId);
      if (!before) {
        out.push({ label: `Slab "${slab.name}"`, before: "—", after: "new" });
        continue;
      }
      add(`${before.name} · name`, before.name, slab.name);
      add(`${before.name} · from`, before.rangeStart, Number(slab.rangeStart));
      add(`${before.name} · to`, before.rangeEnd, Number(slab.rangeEnd));
      add(`${before.name} · reward`, before.reward, Number(slab.reward));
    }
    return out;
  };

  const renderReview = () => {
    const ordered = [...slabs].sort((a, b) => Number(a.rangeStart) - Number(b.rangeStart));
    const changes = computeChanges();
    const row = (label, value) => (
      <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
        <span className="text-sm text-gray-500">{label}</span>
        <span className="text-sm font-medium text-gray-900">{value}</span>
      </div>
    );
    return (
      <SectionCard title="Review" desc={STEPS[3].desc}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            {row("User Type", selectedUser.name)}
            {row("Primary Scheme", selectedScheme ? `#${selectedScheme.id} — ${selectedScheme.name}` : "—")}
            {row("Run Window", `${startDate} → ${endDate}`)}
            {row("Progression", mode === "lifetime" ? "Lifetime" : "Period Based")}
            {mode === "periodic" && row("Period Length", `${periodDays} days (${periods.length} periods)`)}
            {row("Payout", payoutMode === "realtime" ? "Realtime" : "End of Period")}
            {row("Reward Type", rewardType === "percentage" ? "Percentage" : "Fixed Points")}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Slabs</h3>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">Slab</th>
                    <th className="text-left px-3 py-2">Range</th>
                    <th className="text-right px-3 py-2">{rewardLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((s, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-800">{s.name || "—"}</td>
                      <td className="px-3 py-2 text-gray-600">
                        {s.rangeStart} – {s.rangeEnd}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {s.reward}
                        {rewardType === "percentage" ? "%" : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {isEdit && (
          <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-medium text-gray-800 mb-2">
              {changes.length === 0 ? "No changes yet" : `${changes.length} change${changes.length === 1 ? "" : "s"}`}
            </p>
            {changes.length > 0 && (
              <ul className="space-y-1">
                {changes.map((c, i) => (
                  <li key={i} className="text-xs text-gray-600 flex flex-wrap gap-1">
                    <span className="font-medium text-gray-800">{c.label}:</span>
                    <span className="line-through text-gray-400">{String(c.before ?? "—")}</span>
                    <span>→</span>
                    <span className="font-semibold text-gray-900">{String(c.after ?? "—")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!isEdit && (
        <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-800 font-medium">This config freezes once it goes live.</p>
          <p className="text-xs text-amber-700 mt-1">
            Creating also activates the membership. From the first scan that accrues against it,
            nothing here can be edited — period lengths and slab boundaries decide how past
            payouts were calculated, so changing them would rewrite history. To change anything
            after that, create a new membership.
          </p>
        </div>
        )}
      </SectionCard>
    );
  };

  // Edit mode gates: don't render a form over data that hasn't loaded, and don't
  // render one at all for a config the API will refuse to change.
  if (isEdit && loadingExisting) {
    return (
      <div className="min-h-screen w-full bg-gray-50 flex flex-col">
        <div className="w-full px-6 pt-4 pb-3 bg-white border-b border-gray-200">
          <h1 className="text-xl font-semibold text-gray-900">Update Scheme Membership</h1>
          <LinksComponent />
        </div>
        <div className="w-full px-6 py-10 text-sm text-gray-500">Loading membership…</div>
      </div>
    );
  }

  if (isEdit && lockInfo?.locked) {
    return (
      <div className="min-h-screen w-full bg-gray-50 flex flex-col">
        <div className="w-full px-6 pt-4 pb-3 bg-white border-b border-gray-200">
          <h1 className="text-xl font-semibold text-gray-900">Update Scheme Membership</h1>
          <LinksComponent />
        </div>
        <div className="w-full px-6 py-5">
          <SectionCard title="This membership is locked">
            <p className="text-sm text-gray-700">{lockInfo.reason}</p>
            <p className="text-sm text-gray-500 mt-3">
              Period boundaries come from <code className="text-xs">scheme_start</code> and{" "}
              <code className="text-xs">period_days</code>, and slab ranges decide which band a
              user landed in. Editing either would change how payouts that have already been
              made were calculated, so the API refuses it.
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => navigate(`/genefied/${getVendorName()}/membership`)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back to list
              </button>
              <button
                type="button"
                onClick={() => navigate(`/genefied/${getVendorName()}/membership/create`)}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Create a new membership
              </button>
            </div>
          </SectionCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 flex flex-col">
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
        <div className="w-full px-6 pt-4 pb-3">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {isEdit ? "Update Scheme Membership" : "Create Scheme Membership"}
              </h1>
              <LinksComponent />
            </div>
            <div className={`flex items-center gap-2 ${isEdit ? "hidden" : ""}`}>
              {lastSavedAt && (
                <span className="text-xs text-gray-400 hidden md:inline">
                  Saved {new Date(lastSavedAt).toLocaleTimeString()}
                </span>
              )}
              <button
                type="button"
                onClick={() => persistDraft()}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Save draft
              </button>
              <button
                type="button"
                onClick={() => {
                  setDrafts(listDrafts());
                  setShowDrafts(true);
                }}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Drafts ({drafts.length})
              </button>
            </div>
          </div>
          <WizardStepper steps={STEPS} current={currentStep} onStep={(i) => i <= currentStep && goToStep(i)} />
        </div>
      </div>

      <div className="w-full px-6 py-5 flex-1">
        {currentStep === 0 && renderScope()}
        {currentStep === 1 && renderModes()}
        {currentStep === 2 && renderSlabs()}
        {currentStep === 3 && renderReview()}
      </div>

      <div className="sticky bottom-0 bg-white border-t border-gray-200 z-20">
        <div className="w-full px-6 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
          <span className="text-sm text-gray-500">
            Step {currentStep + 1} of {STEPS.length}
          </span>
          {currentStep < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-60 hover:bg-blue-700 transition-colors"
            >
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create & Activate"}
            </button>
          )}
        </div>
      </div>

      {showDrafts && (
        <ModalComponent size="md" show={showDrafts} setShow={setShowDrafts}>
          <div className="p-4 w-full">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Saved drafts</h3>
            {drafts.length === 0 && <p className="text-sm text-gray-500">No drafts yet.</p>}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {drafts.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-800">{d.name}</div>
                    <div className="text-xs text-gray-500">
                      Step {(d.currentStep ?? 0) + 1} · {new Date(d.updatedAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-3 py-1 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                      onClick={() => applyDraft(d)}
                    >
                      Resume
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1 text-xs rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        deleteDraft(d.id);
                        setDrafts(listDrafts());
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ModalComponent>
      )}
    </div>
  );
};

export default MembershipWizard;
